import React, { useEffect, useState } from 'react';
import axios, { AxiosError, AxiosResponse } from 'axios';

import { Link, useLocation } from 'react-router-dom';

import { useMicrosoft } from '../../_libs/hooks/useMicrosoft';
import { UserType } from '../../_libs/microsoft/types';
import StandardPageTemplate from '../../templates/StandardPage';

interface IOAuthError {
  type: string;
  subcode: string;
  description: string;
}
const OAuthPage = (
  { 
    type 
  } : {
    type: UserType;
  }
): JSX.Element => {
  const { search } = useLocation();
  const {
    // isPublisher, // TODO: Add isPublisher state based on authenticated Publisher user
    isAuthenticated,
    setBearerToken,
  } = useMicrosoft();
  const [error, setError] = useState<IOAuthError>();
  const query = new URLSearchParams(search);

  useEffect(() => {
    if (error || isAuthenticated) {
      return;
    }
    if (query) {
      // An error occurred during the SSO flow and Microsoft returned the error and description
      if (query.get('error')) {
        setError({
          type: query.get('error'),
          subcode: query.get('error_subcode'),
          description: query.get('error_description'),
        });
      }
    }
  }, [query]);

  useEffect(() => {
    // The user was able to successfully SSO into Microsoft and Microsoft returned an Authorization code
    // Only trigger this event once to prevent the code from being redeemed twice and returning an error
    if (query && query.get('code')) {
      axios
        .post(`/apis/microsoft/auth/token_for_${type}/`, {
          code: query.get('code'),
        })
        .then(
          (res: AxiosResponse) => {
            if (res.data.status === 'OK' && res.data.authentication) {
              setBearerToken(res.data.authentication);
            } else {
              // Handle error
              const error = res.data.error;
              setError({
                type: error.errorCode,
                subcode: error.subCode,
                description: error.message,
              });
            }
          },
          (err: AxiosError) => {
            // Handle error
            setError({
              type: 'Error',
              subcode: 'Unknown error',
              description: JSON.stringify(err),
            });
          },
        );
    }
  }, []);

  let header = <h1 className="f-serif f-normal f-italic">Attempting to Verify Your Account...</h1>;
  let content =
    <div>
      This page is attempting to redirect you to a Microsoft Single Sign-On page.
    </div>;
  
  if (error) {
    header = <h1 className="f-serif f-normal f-italic">There was an error authenticating your account.</h1>;
    content =
      <div className="f-medium outline br-1 pa-4">
        <div className="f-mono f-small mb-2">Error Details</div>
        <div className="mb-2">
          <strong>Error type:</strong> {error.type}</div>
        <div className="mb-2">
          <strong>Subcode:</strong> {error.subcode}</div>
        <div className="mb-2">
          <strong>Description:</strong> {error.description}</div>
        <button
          type="button"
          className="mb-0 mt-2"
          onClick={() => {
            // Navigation to the OAuthPage can come from multiple sources
            // TODO: Replace history.back() by using state in Link that refers user to /oauth... <Link state={{ referrer: 'VALUE_HERE' }} />; could lead to automatic redirects
            history.back();
          }}>
            Try to Sign in Again
        </button>
      </div>;
  }
  else if (isAuthenticated) {
    header = <h1 className="f-serif f-normal f-italic">Authentication successful.</h1>;
    content = 
      <div>
        {/* 
          Subscribers w/o landing_page tokens will end up at this endpoint...
          // TODO: Replace by using state in Link that refers user to /oauth... <Link state={{ referrer: 'VALUE_HERE' }} />; could lead to automatic redirects
        */}
        <Link to={(type === 'subscriber') ? '/microsoft/subscriber/landing_page/' : '/microsoft/publisher/' } className="button td-none">Return to Previous Page</Link>
      </div>;
  }


  return (
    <StandardPageTemplate>
      <p className="f-small f-serif" style={{ textTransform: 'capitalize' }}>{type}</p>
      {header}
      {content}
    </StandardPageTemplate>
  );
};

export default OAuthPage;
